import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DateTime } from 'luxon';
import {
  AnalyticsInterval,
  AnalyticsRangeContext,
  DynamicQueryContext,
  DynamicQueryRecord,
  RawQueryContext,
} from '@services/analytics-services/core/models';
import { ApiService } from '@services/core-services/api.service';
import { ApiResponse, PaginatedList } from '@services/core-services/models';
import { formatToClickHouseDate } from '@services/core-services/utils/date.utils';

@Injectable({ providedIn: 'root' })
export class DynamicQueryService {
  constructor(private readonly api: ApiService) {}

  fetch(
    rangeContext: AnalyticsRangeContext,
    queryContext: DynamicQueryContext
  ): Observable<PaginatedList<any>> {
    return this.api
      .post<
        any,
        ApiResponse<any>
      >(`analytics/dynamic-query?${this.buildPeriodQuery(rangeContext, false)}`, queryContext)
      .pipe(
        catchError(error => {
          return throwError(error);
        }),
        map(response => ({
          list: response.content.data as DynamicQueryRecord[],
          pagination: response.content.pagination,
        }))
      );
  }

  fetchWithCompare(
    rangeContext: AnalyticsRangeContext,
    queryContext: DynamicQueryContext
  ): Observable<PaginatedList<any>> {
    return this.api
      .post<
        any,
        ApiResponse<any>
      >(`analytics/dynamic-query?${this.buildPeriodQuery(rangeContext, true)}`, queryContext)
      .pipe(
        catchError(error => {
          return throwError(error);
        }),
        map(response => ({
          list: response.content.data as DynamicQueryRecord[],
          pagination: response.content.pagination,
        }))
      );
  }

  fetchRaw(
    range: AnalyticsRangeContext,
    context: RawQueryContext,
    timezone?: string
  ): Observable<PaginatedList<any>> {
    return this.api
      .post<
        any,
        ApiResponse<any>
      >(`analytics/raw-query`, this.applyParams(range, context, false, timezone))
      .pipe(
        catchError(error => {
          return throwError(error);
        }),
        map(response => ({
          list: response.content.data as DynamicQueryRecord[],
          pagination: response.content.pagination,
        }))
      );
  }

  fetchRawWithCompare(
    range: AnalyticsRangeContext,
    context: RawQueryContext,
    timezone?: string
  ): Observable<PaginatedList<any>> {
    return this.api
      .post<
        any,
        ApiResponse<any>
      >(`analytics/raw-query`, this.applyParams(range, context, true, timezone))
      .pipe(
        catchError(error => {
          return throwError(error);
        }),
        map(response => ({
          list: response.content.data as DynamicQueryRecord[],
          pagination: response.content.pagination,
        }))
      );
  }

  /**
   * Gets the user's timezone or returns a default
   * @returns {string} A valid timezone string
   */
  getUserTimezone(): string {
    // This would ideally get the user preference from a service or browser
    // For now, use the browser's timezone or fallback to a default
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
    } catch (error) {
      return 'Asia/Kolkata'; // Default fallback
    }
  }

  toLocalTimezone(localDate: Date, timezone: string = '') {
    const userTimezone = timezone || this.getUserTimezone();
    // Remove the 'Z' from ISO string as ClickHouse cannot parse it properly
    const isoString = localDate.toISOString().replace('Z', '');
    return `toTimeZone(toDateTime64('${isoString}', 3 ), '${userTimezone}')`;
  }

  private applyParams(
    rangeContext: AnalyticsRangeContext,
    context: RawQueryContext,
    withCompare: boolean,
    timezone?: string
  ) {
    if (context.raw_query) {
      const { date_from, date_to, interval } = this.getRangeQuery(rangeContext, withCompare);
      const userTimezone = timezone || this.getUserTimezone();

      context.raw_query = context.raw_query
        .replace(/\s+/g, ' ')
        .replace(/{{date_from}}/g, this.toLocalTimezone(new Date(date_from), userTimezone))
        .replace(/{{date_to}}/g, this.toLocalTimezone(new Date(date_to), userTimezone))
        .replace(/{{interval}}/g, interval.toString())
        .replace(/\n/g, ' ');
    }
    return context;
  }

  private buildPeriodQuery(rangeContext: AnalyticsRangeContext, withCompare: boolean) {
    const { interval, date_to, date_from } = this.getRangeQuery(rangeContext, withCompare);
    const result = [];
    result.push(`date_from=${date_from}`);
    result.push(`date_to=${date_to}`);
    result.push(`interval=${interval}`);
    return result.join('&');
  }

  private getRangeQuery(rangeContext: AnalyticsRangeContext, withCompare: boolean) {
    const { range, interval, compare } = rangeContext;
    const start = withCompare && compare ? compare.start : range.start;
    const dateFrom = start || DateTime.now().minus({ days: 30 }).toJSDate();
    const dateTo = range.end ? range.end : DateTime.now().toJSDate();
    return {
      date_from: formatToClickHouseDate(dateFrom),
      date_to: formatToClickHouseDate(dateTo),
      interval: interval || AnalyticsInterval.Hour,
    };
  }
}
